import { FC, useEffect, useState } from 'react';
import { Modal, ModalProps, Button, FormInput, FormCheckbox } from '@praktio/common-ui';
import { Form } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { PathMatch, matchPath, useLocation } from 'react-router-dom';

import { RootState } from '../../../ducks/store';
import { submitLearnerFeedback } from '../../../ducks/learner';
import { ModuleTypes } from '../../../utils';

import './FeedbackModal.scss';

interface IRouteParams {
  courseId: string;
  moduleId: string;
}

interface IProps extends ModalProps {
  show: boolean;
  onHide: () => void;
  errorMessage?: string;
}

const getParams = (pathname: string) => {
  // Constraints in react-router v6 don't allow for regex or optional params in the path,
  // so we have to step through patterns until we get a match (or nothing if there weren't matches)
  const patterns = [
    '/courses/:courseId/modules/:moduleId/slides/:slideId',
    '/courses/:courseId/modules/:moduleId',
    '/courses/:courseId',
  ];
  let matches: PathMatch<string> | null = null;
  for (const pattern of patterns) {
    matches = matchPath(pattern, pathname);
    if (matches) {
      break;
    }
  }

  // Have to cast as unknown first or else we get a warning about Params<string> being uncompatible with IRouteParams
  const params = (matches?.params || {}) as unknown as IRouteParams;
  return params;
};

const FeedbackModal: FC<IProps> = ({ show, onHide, errorMessage }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { courseId, moduleId } = getParams(location.pathname);
  const [hideForScreenshot, setHideForScreenshot] = useState(false);
  const [canvasDataUrl, setCanvasDataUrl] = useState<string>();
  const [message, setMessage] = useState('');
  const [noScreenshot, setNoScreenshot] = useState(false);

  const { slideId, course } = useSelector((state: RootState) => {
    const { learner } = state;
    const { currentSlideId, course } = learner;
    return {
      slideId: currentSlideId,
      course,
    };
  });

  useEffect(() => {
    if (!show) {
      setHideForScreenshot(false);
      setCanvasDataUrl(undefined);
      setMessage('');
      setNoScreenshot(false);
    }
  }, [show]);

  const onSave = () => {
    const currentModule = course?.modules?.find((m) => m._id === moduleId);
    const isAuthorModule = currentModule?.type === ModuleTypes.Author;
    dispatch(
      submitLearnerFeedback({
        message,
        screenshot: canvasDataUrl,
        courseId,
        moduleId,
        slideId: isAuthorModule ? slideId : undefined,
        errorMessage,
      }),
    );
  };

  const takeScreenshot = (stream: MediaStream) => {
    const track = stream.getVideoTracks()[0];
    const imageCapture = new ImageCapture(track);
    imageCapture
      .grabFrame()
      .then((frame: any) => {
        const canvas = document.createElement('canvas');
        canvas.width = frame.width;
        canvas.height = frame.height;
        const context2d = canvas.getContext('2d');
        if (context2d) {
          context2d.drawImage(frame, 0, 0);
          stream.getTracks().forEach((track) => track.stop());
          setCanvasDataUrl(canvas.toDataURL());
          setHideForScreenshot(false);
        }
      })
      .catch(() => {
        setHideForScreenshot(false);
      });
  };

  const onScreenshotClick = () => {
    setHideForScreenshot(true);
    const displayMediaOptions = { video: true, audio: false };
    navigator.mediaDevices
      .getDisplayMedia(displayMediaOptions)
      .then((s) => {
        setTimeout(takeScreenshot.bind(this, s), 500);
      })
      .catch((e) => {
        setHideForScreenshot(false);
      });
  };

  return (
    <Modal
      show={hideForScreenshot ? false : show}
      onHide={onHide}
      onSave={onSave}
      title="Send a Message to Praktio"
      description="Submit your message below to send an email to Praktio. We'll reply by email soon."
      footerAdditions={
        <div className="FeedbackModal-footer">
          <span className="required-notice">* required</span>
        </div>
      }
      className="FeedbackModal"
      saveText="SEND"
      disableSave={!message || (!noScreenshot && !canvasDataUrl)}
    >
      <Form id="feedback-modal-form" onSubmit={(e) => e.preventDefault()}>
        <FormInput
          formGroupClassName="FeedbackModal-message"
          value={message}
          label="Message"
          placeholder="Include as much detail as possible."
          type="textarea"
          onChange={(e: any) => setMessage(e.target.value || '')}
          rows={5}
          required
        />
        <Form.Group className="FeedbackModal-screenshot">
          <Form.Label>Screenshot{!noScreenshot ? ' *' : ''}</Form.Label>
          <div className="info">
            Screenshots are usually essential for us to understand your message.
          </div>
          <div className="d-flex">
            <div className="left-half">
              <Button onClick={onScreenshotClick} className="secondary">
                <i className="material-icons">screen_share</i>
                Take Screenshot
              </Button>
              <FormCheckbox
                id="screenshot-check"
                className="info"
                label="A screenshot is not relevant to my feedback"
                onChange={() => setNoScreenshot(!noScreenshot)}
                checked={noScreenshot}
              />
            </div>
            {canvasDataUrl && (
              <div className="thumbnail">
                <img className="image" alt="screenshot" src={canvasDataUrl} />
                <div className="clear-icon" onClick={() => setCanvasDataUrl(undefined)}>
                  <i className="material-icons">close</i>
                </div>
              </div>
            )}
          </div>
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default FeedbackModal;
